import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircle,
  DeliveryDining,
  DoneAll,
  LocalShipping,
  PendingActions,
  Storefront,
} from "@mui/icons-material";
import {
  acceptDeliveryOrder,
  markDeliveryOrderReady,
  selectDeliveryOrders,
  selectDeliverySources,
  DELIVERY_STATUS,
} from "../Store/Slices/deliveryOrdersSlice";
import styles from "./DeliveryOrdersPage.module.css";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diffMins = Math.max(
    0,
    Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  );
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  const hrs = Math.floor(diffMins / 60);
  return `${hrs}h ${diffMins % 60}m ago`;
};

const DeliveryOrdersPage = () => {
  const dispatch = useDispatch();
  const orders = useSelector(selectDeliveryOrders);
  const sources = useSelector(selectDeliverySources);
  const currency = useSelector((state) => state.currency);
  const symbol = currency?.currencySymbol || "AED";
  const [sourceFilter, setSourceFilter] = useState("All");

  const filteredOrders = useMemo(() => {
    const list =
      sourceFilter === "All"
        ? orders
        : orders.filter((order) => order.source === sourceFilter);

    const rank = {
      [DELIVERY_STATUS.PENDING]: 0,
      [DELIVERY_STATUS.ACCEPTED]: 1,
      [DELIVERY_STATUS.READY]: 2,
    };

    return list.slice().sort((a, b) => {
      const stateOrder = (rank[a.status] ?? 99) - (rank[b.status] ?? 99);
      if (stateOrder !== 0) return stateOrder;
      return new Date(a.placedAt || 0) - new Date(b.placedAt || 0);
    });
  }, [orders, sourceFilter]);

  const counts = useMemo(() => {
    const pending = filteredOrders.filter(
      (order) => order.status === DELIVERY_STATUS.PENDING
    ).length;
    const accepted = filteredOrders.filter(
      (order) => order.status === DELIVERY_STATUS.ACCEPTED
    ).length;
    const ready = filteredOrders.filter(
      (order) => order.status === DELIVERY_STATUS.READY
    ).length;

    return { pending, accepted, ready, total: filteredOrders.length };
  }, [filteredOrders]);

  const statusMeta = {
    [DELIVERY_STATUS.PENDING]: {
      label: "Pending",
      cls: styles.pendingTag,
      icon: <PendingActions fontSize="inherit" />,
    },
    [DELIVERY_STATUS.ACCEPTED]: {
      label: "Accepted",
      cls: styles.acceptedTag,
      icon: <LocalShipping fontSize="inherit" />,
    },
    [DELIVERY_STATUS.READY]: {
      label: "Ready",
      cls: styles.readyTag,
      icon: <CheckCircle fontSize="inherit" />,
    },
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <DeliveryDining /> Delivery Orders
          </h1>
          <p className={styles.subtitle}>
            Website and app orders (Zomato, Talabat, Smile, Careem, Others)
          </p>
        </div>
      </div>

      <div className={styles.filtersRow}>
        {["All", ...sources].map((source) => (
          <button
            key={source}
            className={`${styles.sourceChip} ${
              sourceFilter === source ? styles.sourceChipActive : ""
            }`}
            onClick={() => setSourceFilter(source)}
          >
            {source}
          </button>
        ))}
      </div>

      <div className={styles.summaryRow}>
        <div className={styles.summaryCard}>
          <span>Total</span>
          <b>{counts.total}</b>
        </div>
        <div className={`${styles.summaryCard} ${styles.pendingBorder}`}>
          <span>Pending</span>
          <b>{counts.pending}</b>
        </div>
        <div className={`${styles.summaryCard} ${styles.acceptedBorder}`}>
          <span>Accepted</span>
          <b>{counts.accepted}</b>
        </div>
        <div className={`${styles.summaryCard} ${styles.readyBorder}`}>
          <span>Ready</span>
          <b>{counts.ready}</b>
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className={styles.emptyState}>
          <DoneAll className={styles.emptyIcon} />
          <p>No delivery orders for the selected source.</p>
        </div>
      ) : (
        <div className={styles.ordersGrid}>
          {filteredOrders.map((order) => {
            const meta = statusMeta[order.status];
            return (
              <article key={order.id} className={styles.orderCard}>
                <div className={styles.cardHead}>
                  <div>
                    <p className={styles.orderNo}>{order.orderNo}</p>
                    <p className={styles.customer}>{order.customerName}</p>
                  </div>
                  <span className={styles.sourceBadge}>
                    <Storefront fontSize="inherit" /> {order.source}
                  </span>
                </div>

                <p className={styles.address}>{order.address}</p>

                <div className={styles.itemsWrap}>
                  {order.items.map((item) => (
                    <div key={`${order.id}-${item.name}`} className={styles.itemRow}>
                      <span>{item.name}</span>
                      <span>{item.qty}x</span>
                    </div>
                  ))}
                </div>

                <div className={styles.metaRow}>
                  <span className={`${styles.statusTag} ${meta.cls}`}>
                    {meta.icon} {meta.label}
                  </span>
                  <span className={styles.timeText}>{timeAgo(order.placedAt)}</span>
                </div>

                <div className={styles.actionRow}>
                  <button
                    className={styles.acceptBtn}
                    disabled={order.status !== DELIVERY_STATUS.PENDING}
                    onClick={() => dispatch(acceptDeliveryOrder(order.id))}
                  >
                    Accept
                  </button>
                  <button
                    className={styles.readyBtn}
                    disabled={order.status !== DELIVERY_STATUS.ACCEPTED}
                    onClick={() => dispatch(markDeliveryOrderReady(order.id))}
                  >
                    Ready
                  </button>
                </div>

                <div className={styles.totalRow}>
                  <span>Total</span>
                  <b>
                    {symbol} {Number(order.total || 0).toFixed(2)}
                  </b>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DeliveryOrdersPage;
